import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { callApi } from '../lib/api.ts';
import { prepareImageUpload } from '../lib/imageDownscale.ts';
import {
  readSections,
  orderedSections,
  builtPageAnchors,
  resolveUploadSection,
  resolveSectionChoice,
  NEW_SECTION,
  readPages,
  activePageId as activePageIdOf,
  siteAllLocked,
  pageStatus,
  HOME_PAGE_ID,
  type Pages,
} from '../lib/sections.ts';
import { nearBottom, shouldAutoScroll } from '../lib/chatScroll.ts';
import { BuildStatus } from './BuildStatus.tsx';
import { PageManager } from './PageManager.tsx';
import { SeoSummary, type SeoClientSummary } from './SeoSummary.tsx';
import type { Client } from '../lib/types.ts';
import { friendlyError } from '../lib/friendlyError.ts';
import { btnSolid, btnGhost, input as inputCls, sectionDesc } from '../lib/ui.ts';
import { FlapText } from './FlapText.tsx';

/**
 * The continuous-chat UI (chat-engine.md §6) — ONE component, two instances
 * (build | seo), the conversational replacement for both one-shot forms. A
 * persistent thread + one input pinned at the bottom + attach-mid-thread; each
 * send is a turn (chat-turn → poll chat-turn-status → reload the thread). The
 * agent replies, pushes back, or takes the instance's action:
 *   - Build: builds the active section; a section rail (per-section CF preview +
 *     Lock) reuses build-chat-status / build-chat-lock — no new lock code.
 *   - SEO: finalizes the handoff doc; no section rail. ALLOWED on live clients
 *     (the §7 carve-out — it writes only docs/clients/<slug>-seo.md), and shows
 *     the client-presentable summary once a handoff is verified.
 */

interface ChatMessage {
  id: string;
  role: 'user' | 'agent';
  content: string;
  attachments: string[];
  meta: Record<string, unknown>;
  /** IA-3: the page this message targeted — NULL/'home' = Home; drives the page badge. */
  pageId?: string | null;
  createdAt: string;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const fileName = (p: string) => p.split('/').pop() ?? p;

// #GR6: the coarse build phase surfaced by the poll → a human label for the banner.
// deploy/green are shown by the section rail's CF status after the turn.
type BuildPhase = 'thinking' | 'reading' | 'writing' | 'committing';
const PHASE_LABEL: Record<BuildPhase, string> = {
  thinking: 'Thinking…',
  reading: 'Reading your brand kit, artboard & media…',
  writing: 'Writing the section…',
  committing: 'Committing — Cloudflare will build it next…',
};
// #GR40: only a REAL build phase means a build is in flight — 'thinking' (the fast
// reply-vs-act decision turn) is NOT a build, and neither is "no phase yet".
const isBuildingPhase = (p: BuildPhase | null): boolean => p != null && p !== 'thinking';

// An in-flight turn, persisted so navigating away + back re-attaches to it (the
// build runs to completion server-side regardless — this is visibility only).
interface ActiveTurn {
  buildId: string;
  sectionId?: string;
  sectionName?: string;
  /** M-P3: the page this turn is building (Home default) — re-attach restores the page. */
  pageId?: string;
  /** IA-4/#GR58: this turn is one leg of a whole-site fan-out → status reads "Applying
   *  site change · <page>", not "Building <anchor section>" (which would mislead). */
  siteOp?: boolean;
}
// IA-1: a send captured while a turn is running, to auto-fire when the queue drains.
interface QueuedSend {
  id: string;
  message: string;
  files: File[];
  sectionId?: string;
  sectionName: string;
  turnPage?: string;
  /** Human summary for the "queued" chip (the section name, or "chat"). */
  label: string;
  /** IA-4: one leg of a site-level fan-out (a Whole-site send → one turn per built page). */
  siteOp?: boolean;
}

const turnKey = (clientId: string, instance: string) => `alchmy:activeTurn:${clientId}:${instance}`;
function loadSavedTurn(clientId: string, instance: string): ActiveTurn | null {
  try {
    const raw = localStorage.getItem(turnKey(clientId, instance));
    return raw ? (JSON.parse(raw) as ActiveTurn) : null;
  } catch {
    return null;
  }
}
function saveTurn(clientId: string, instance: string, turn: ActiveTurn | null) {
  try {
    if (turn) localStorage.setItem(turnKey(clientId, instance), JSON.stringify(turn));
    else localStorage.removeItem(turnKey(clientId, instance));
  } catch {
    /* storage unavailable — re-attach degrades, the build still completes server-side */
  }
}

// #GR25/#GR10: in-memory per-(client,instance) view cache so returning to the Build step
// paints INSTANTLY from cache (revalidate in the background — no "Loading…" flash) and
// the composer draft + section pick + scroll position survive navigation. Module-scoped,
// so it outlives the component unmount for the app session (a hard reload refetches).
interface ChatViewCache {
  messages: ChatMessage[];
  input: string;
  sectionName: string;
  sectionChoice: string;
  activeSectionId: string | null;
  scrollTop: number;
}
const viewCache = new Map<string, ChatViewCache>();
// IA-3: the Build thread is now UNIFIED across pages → the view cache is per-(client,
// instance), no longer per-page (was `:${pageId}`). One continuous conversation; switching
// page no longer swaps the visible thread. SEO was always single-thread.
const viewKey = (clientId: string, instance: string) => `${clientId}:${instance}`;

// Mirror the server caps so we catch them client-side (nicer than a 422 round-trip).
const MAX_MESSAGE_CHARS = 8_000;
const MAX_ATTACHMENTS = 8;
// Code-reference attachments (code-reference-attachments.md) — the endpoint's UTF-8 text/code
// allow-list, as an <input accept> string. The `references` endpoint re-validates + caps.
const REFERENCE_ACCEPT = '.txt,.md,.html,.htm,.css,.scss,.js,.jsx,.ts,.tsx,.json,.vue,.svelte,.astro';

export function BuildChat({
  client,
  instance = 'build',
  onActivity,
  onContinue,
  onSkip,
  canMaintainLive = false,
}: {
  client: Client;
  instance?: 'build' | 'seo';
  onActivity: () => void;
  onContinue: () => void;
  /** SEO only: advance past this optional step WITHOUT marking it done. */
  onSkip?: () => void;
  /** go-live-protection §3a: an admin maintaining a maintainable live client — the backend
   *  liveGuard('site-write') allow-condition. True → the chat works (edits preview on the branch). */
  canMaintainLive?: boolean;
}) {
  const isSeo = instance === 'seo';
  // go-live-protection §3a (PR2 tightening): maintenance mode opens chat on a LIVE client to an
  // ADMIN only — the edit lands on the preview branch. Replaces the old SEO-for-anyone carve-out
  // (`!isSeo`): the backend now 403s a non-admin SEO chat on a live client too, so the frontend
  // matches exactly (admin+maintainable → allowed for build AND seo; else blocked).
  const liveBlocked = client.is_live && !canMaintainLive;

  // ── Multi-page (M-P2): the page manager + a page-scoped rail (Build only) ────
  // activePage is the rail's current page (persisted in wizard_state.activePageId). The
  // page row always shows Home; a legacy/empty client (no pages layer yet) shows a single
  // synthetic Home chip and builds Home-scoped exactly as before.
  const [activePage, setActivePage] = useState<string>(() => activePageIdOf(client.wizard_state));
  const pages = useMemo<Pages>(() => (isSeo ? {} : readPages(client.wizard_state)), [isSeo, client.wizard_state]);
  const hasPageLayer = Object.keys(pages).length > 0;
  // If the active page was deleted / never existed, fall back to Home. (The page chips +
  // the synthetic-Home fallback now live in the shared PageManager component — IA-2.)
  const effectivePage = hasPageLayer && pages[activePage] ? activePage : HOME_PAGE_ID;
  const isHomePage = !hasPageLayer || effectivePage === HOME_PAGE_ID || pages[effectivePage]?.isHome === true;

  const sections = useMemo(() => readSections(client.wizard_state, effectivePage), [client.wizard_state, effectivePage]);
  const list = isSeo ? [] : orderedSections(sections);

  // IA-3 (unified thread): label a message's page + section for its badges. pageId
  // NULL/'home' → "Home"; else the page's name (falls back to /slug, then the raw id).
  // Section name is looked up across ALL pages' sections (a unified bubble can be from any
  // page), with the Home/legacy flat sections as the fallback. Pure derivations from the
  // wizard_state we already read — no extra fetch.
  function pageLabel(pageId?: string | null): string {
    if (!pageId || pageId === HOME_PAGE_ID) return 'Home';
    const p = pages[pageId];
    return p ? p.name : `/${pageId}`;
  }
  function sectionLabel(pageId: string | null | undefined, sectionId: string): string {
    const pageSections = !pageId || pageId === HOME_PAGE_ID ? readSections(client.wizard_state, HOME_PAGE_ID) : pages[pageId]?.sections;
    return pageSections?.[sectionId]?.name ?? sectionId;
  }
  // IA-4: the site-level fan-out target set (built pages + their anchor sections) — a pure,
  // tested helper (multi-page.md §14). See builtPageAnchors in ../lib/sections.ts.
  // Whole-SITE gate (§2): Home ≥1 locked + every page with sections fully locked. For a
  // legacy/single-page client this equals allLocked(Home) — unchanged behaviour.
  const pagesForGate: Pages = hasPageLayer
    ? pages
    : { [HOME_PAGE_ID]: { slug: 'home', name: 'Home', order: 0, inNav: true, isHome: true, sections } };
  const everyLocked = !isSeo && siteAllLocked(pagesForGate);
  // Item 3 (Phase 4): name WHICH page(s) still block the whole-site gate so the "not yet"
  // message is actionable ("Lock every section on Home and Work") instead of generic. A pure
  // DISPLAY derivation from the SAME pagesForGate the gate reads — mirrors siteAllLocked's
  // condition (Home must be fully locked; a non-home page blocks only when it's a draft, i.e.
  // has sections but not all locked; an empty non-home page never gates). The gate logic
  // (everyLocked / siteAllLocked) is unchanged.
  const blockingPages = Object.entries(pagesForGate)
    .filter(([id, p]) => {
      const isHome = id === HOME_PAGE_ID || p.isHome;
      const status = pageStatus(p); // 'empty' | 'draft' | 'locked'
      return isHome ? status !== 'locked' : status === 'draft';
    })
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([id]) => pageLabel(id));
  // SEO "done" = a verified handoff (the wizard_state.seo marker the turn writes).
  const seoDone = !!(client.wizard_state as { seo?: { handoffReady?: boolean } } | null)?.seo?.handoffReady;

  // #GR25/#GR10: seed from the view cache so the FIRST paint is instant (no "Loading…"
  // flash) and the composer draft + section pick survive a remount. IA-3: one unified thread
  // per (client, instance), no longer per page.
  const seed = viewCache.get(viewKey(client.id, instance));
  const [messages, setMessages] = useState<ChatMessage[]>(seed?.messages ?? []);
  const [loading, setLoading] = useState(!seed);
  const [input, setInput] = useState(seed?.input ?? '');
  const [files, setFiles] = useState<File[]>([]);
  const [sectionName, setSectionName] = useState(seed?.sectionName ?? '');
  // #GR24: the section picker value — '' (just chat) | an existing section id | NEW_SECTION.
  // Persists across sends so the designer picks a section ONCE and every turn targets it
  // (which keeps #GR26A's sticky artboard re-grounding every turn — "set once, grounded
  // forever"). sectionName is only the NEW-section name box.
  const [sectionChoice, setSectionChoice] = useState(seed?.sectionChoice ?? '');
  // #GR26A: the section the designer is actively working (last one targeted), so a drop
  // made with the name field momentarily blank is still filed under that section's
  // artboard folder → sticky. Kept in lock-step with sectionChoice; reset on client change.
  const [activeSectionId, setActiveSectionId] = useState<string | null>(seed?.activeSectionId ?? null);
  // Code-reference attachments (code-reference-attachments.md) — the reference code/text the
  // designer drops for the agent to READ, listed per section (sticky, server-side under
  // .references/<sectionId>/). Not cached in the view seed (server is the source of truth — a
  // list() reloads them when a section is opened/changed).
  const [references, setReferences] = useState<{ name: string }[]>([]);
  const [refBusy, setRefBusy] = useState(false);
  const [refErr, setRefErr] = useState<string | null>(null);
  const refInputRef = useRef<HTMLInputElement>(null);
  // The concrete section references attach to — the picker's resolved target (an existing id, or
  // the slugified NEW-section name), or undefined for "just chat" (no section → can't attach).
  const refSectionId = useMemo(
    () => resolveSectionChoice(sectionChoice, sectionName, sections).sectionId,
    [sectionChoice, sectionName, sections],
  );
  // IA-4: the composer scope — 'page' (default; the active page + picked section) or 'site'
  // (Whole site: a send fans out one build turn per BUILT page, each targeting that page's
  // anchor section, via the IA-1 queue). Ephemeral (reset on client switch + after a fan-out).
  const [scope, setScope] = useState<'page' | 'site'>('page');
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [lockErr, setLockErr] = useState<Record<string, string>>({});
  // The turn currently running (this session or re-attached on return). Drives the
  // persistent "🔨 building…" status + the authoritative in-progress rail preview.
  const [activeTurn, setActiveTurn] = useState<ActiveTurn | null>(null);
  // IA-1: sends captured WHILE a detached turn runs — the composer stays live, so the
  // designer queues the next section (or a reply) instead of waiting ~15–25 min. Drains
  // FIFO: each completed turn auto-fires the next. Concurrency: only ONE turn in flight
  // at a time (the backend also 409-guards, this is the friendly front gate). Ephemeral
  // (not persisted) — a reload keeps the running build (re-attach) but drops the queue.
  const [queue, setQueue] = useState<QueuedSend[]>([]);
  const [stopping, setStopping] = useState(false); // #GR17: Stop clicked, awaiting break
  const [buildPhase, setBuildPhase] = useState<BuildPhase | null>(null); // #GR6: live phase from the poll
  // #GR25 scroll: the thread scroller + whether the reader is near the bottom (only then
  // do new messages auto-scroll — never yank a reader who scrolled up into history) +
  // the last-seen message count (so a remount painting from cache doesn't auto-scroll).
  const threadRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const prevLenRef = useRef(messages.length);

  // SEO: the client-presentable summary from the latest VERIFIED finalize turn
  // (persisted on that agent turn's meta — no separate fetch).
  const seoSummary = useMemo<SeoClientSummary | null>(() => {
    if (!isSeo) return null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const meta = messages[i].meta as { action?: string; verified?: boolean; clientSummary?: SeoClientSummary };
      if (messages[i].role === 'agent' && meta.action === 'seo' && meta.verified && meta.clientSummary) {
        return meta.clientSummary;
      }
    }
    return null;
  }, [isSeo, messages]);

  // forClient guards against a mid-build client switch: a load/poll started for the
  // previous client must NOT clobber the new client's thread when it resolves late.
  // IA-3: the Build thread is UNIFIED (allPages) — one continuous conversation across every
  // page — so the load no longer carries or guards on a page (a page switch never swaps the
  // thread). SEO stays a single (NULL) thread. Guard on client only.
  async function loadThread(forClient: string): Promise<ChatMessage[] | undefined> {
    try {
      const r = await callApi<{ messages: ChatMessage[] }>('chat-messages', {
        method: 'POST',
        body: JSON.stringify({ clientId: forClient, instance, ...(instance === 'build' ? { allPages: true } : {}) }),
      });
      if (clientIdRef.current !== forClient) return undefined;
      setMessages(r.messages);
      return r.messages; // #GR65: callers can read fresh reply meta (sectionRegistered) without a stale closure
    } catch {
      /* transient — keep what we have */
      return undefined;
    } finally {
      if (clientIdRef.current === forClient) setLoading(false);
    }
  }

  // Load the thread on client change, and RE-ATTACH to an in-flight turn if one was
  // persisted (designer navigated away mid-build and came back). The active section
  // field is restored too, so the rail + status stay coherent.
  const clientIdRef = useRef(client.id);
  // IA-1: the SYNCHRONOUS concurrency source of truth — "a turn is running or dispatching".
  // React state (activeTurn/busy) is stale within one event handler, so two rapid sends
  // could both read false and double-fire a build; this ref flips synchronously so the 2nd
  // is queued. activeTurn/busy remain the UI signals; runningRef is the gate.
  const runningRef = useRef(false);
  // IA-1: a synchronous mirror of `queue` so dequeue/fire happens OUTSIDE the setQueue
  // updater (updaters must be pure — StrictMode double-invokes them in dev, which would
  // double-dispatch a build). Kept in lock-step with every setQueue below.
  const queueRef = useRef<QueuedSend[]>([]);
  // IA-3: the thread is unified (loadThread no longer guards on a page), so the M-P3
  // `pageRef` load-guard is gone. This ref just tracks the last (client,page) the
  // page-switch effect saw, so it re-scopes the section picker only on a GENUINE page
  // change (not the initial mount / client switch, which the client effect owns).
  const prevPageRef = useRef(`${client.id}:${effectivePage}`);
  // #GR44: the native <input type="file"> is UNCONTROLLED, so setFiles([]) clears the
  // React state + chips but NOT the element's displayed filename — it holds its last
  // selection until its own .value is reset. Keep a ref so we clear both in lock-step.
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Clear the attachment state AND the native file input together (no stale filename).
  function clearFiles() {
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // ── Code-reference attachments (code-reference-attachments.md) ──────────────
  // Reference code/text is uploaded IMMEDIATELY on drop (not batched on send like artboards)
  // against the picked section, then listed from the server. Sticky per section by construction:
  // the auto-list effect below reloads a section's refs whenever it's opened/changed.
  async function reloadReferences(sectionId: string) {
    try {
      const r = await callApi<{ references: { name: string }[] }>('references', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, sectionId, action: 'list' }),
      });
      setReferences(r.references);
    } catch {
      /* leave the current list; a list hiccup shouldn't blank the UI */
    }
  }
  async function uploadReferences(picked: File[]) {
    setRefErr(null);
    if (picked.length === 0) return;
    const sectionId = refSectionId;
    if (!sectionId) {
      setRefErr('Pick or name a section first — references attach to a section.');
      if (refInputRef.current) refInputRef.current.value = '';
      return;
    }
    setRefBusy(true);
    try {
      // UTF-8 text, sent as `content` (the endpoint prefers it over base64). One POST for all
      // files so the endpoint's ≤8-per-section cap is enforced atomically.
      const files = await Promise.all(picked.map(async (f) => ({ filename: f.name, content: await f.text() })));
      await callApi('references', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, sectionId, action: 'upload', files }),
      });
      await reloadReferences(sectionId);
    } catch (e) {
      setRefErr(friendlyError(e, (e as Error).message)); // surfaces the endpoint's 422/413/403 text plainly
    } finally {
      setRefBusy(false);
      if (refInputRef.current) refInputRef.current.value = ''; // #GR44: clear the native input
    }
  }
  async function deleteReference(name: string) {
    const sectionId = refSectionId;
    if (!sectionId) return;
    setRefBusy(true);
    setRefErr(null);
    try {
      await callApi('references', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, sectionId, action: 'delete', name }),
      });
      await reloadReferences(sectionId);
    } catch (e) {
      setRefErr(friendlyError(e, (e as Error).message));
    } finally {
      setRefBusy(false);
    }
  }
  // Load a section's committed references when an EXISTING section is opened/changed (spec:
  // "the section is opened/changed"). A NEW (unbuilt) section has none to list yet → clear; the
  // upload handler re-lists it explicitly after the first drop. Build-only. Cancel-guarded so a
  // fast switch doesn't land a stale list.
  useEffect(() => {
    if (isSeo) return;
    const sid = sectionChoice && sectionChoice !== NEW_SECTION ? sectionChoice : null;
    setRefErr(null);
    if (!sid) {
      setReferences([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await callApi<{ references: { name: string }[] }>('references', {
          method: 'POST',
          body: JSON.stringify({ clientId: client.id, sectionId: sid, action: 'list' }),
        });
        if (!cancelled) setReferences(r.references);
      } catch {
        if (!cancelled) setReferences([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id, sectionChoice, effectivePage, isSeo]);
  // #GR43: a live mirror of the rail's sections so pollTurn (an async loop with a stale
  // `sections` closure) can tell when THIS turn's new section has materialised.
  const sectionsRef = useRef(sections);
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);
  useEffect(() => {
    clientIdRef.current = client.id;
    // §13.3 re-attach: resume on the in-flight turn's page if re-attaching, else the
    // persisted active page — snaps the rail + indicator to the running build (the unified
    // thread still shows everything).
    const saved = loadSavedTurn(client.id, instance);
    const resumePage = saved?.pageId ?? activePageIdOf(client.wizard_state);
    setActivePage(resumePage);
    prevPageRef.current = `${client.id}:${resumePage}`; // this effect owns the switch; don't re-fire the page effect
    // #GR25/#GR10: rehydrate from cache (instant paint), then revalidate in the
    // background. A cached view shows no "Loading…" flash and keeps the draft + pick.
    // IA-3: the cache is per-(client,instance) — one unified thread.
    const cache = viewCache.get(viewKey(client.id, instance));
    setMessages(cache?.messages ?? []);
    setLoading(!cache);
    setInput(cache?.input ?? '');
    clearFiles(); // #GR44: File objects can't be cached — clear state + the native input
    setReferences([]); // references reload from the server via the auto-list effect for this client
    setRefErr(null);
    setSectionName(cache?.sectionName ?? '');
    // IA-3 edge guard: a cached section pick belongs to whatever page was active when it was
    // stamped. On remount the active page is `resumePage`; if the cached pick isn't a section
    // ON that page, reset it — else the first send would target (resumePage, a stale
    // other-page sectionId). NEW_SECTION (a name being typed) is page-agnostic → keep it.
    const restorePickValid =
      !cache?.sectionChoice ||
      cache.sectionChoice === NEW_SECTION ||
      !!readSections(client.wizard_state, resumePage)[cache.sectionChoice];
    setSectionChoice(restorePickValid ? (cache?.sectionChoice ?? '') : '');
    setActiveSectionId(restorePickValid ? (cache?.activeSectionId ?? null) : null);
    prevLenRef.current = cache?.messages.length ?? 0; // don't auto-scroll on a cache paint
    setErr(null);
    setBusy(false);
    setPhase('');
    setActiveTurn(null);
    setQueue([]); // IA-1: the queue is per-client + ephemeral — drop it on a client switch
    queueRef.current = [];
    runningRef.current = false; // IA-1: reset the concurrency gate for the new client
    setScope('page'); // IA-4: site-scope is ephemeral — start each client on page scope
    void loadThread(client.id); // IA-3: unified thread (allPages for build) — background revalidate
    if (saved) {
      setActiveTurn(saved);
      runningRef.current = true; // IA-1: a re-attached turn holds the gate until it completes
      // #GR24: default the picker to the last-targeted section (the in-flight turn's),
      // so a re-attach keeps building the same section without re-picking.
      if (saved.sectionId) {
        setActiveSectionId(saved.sectionId);
        setSectionChoice(saved.sectionId);
      }
      void watchTurn(saved, client.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  // IA-3: switching PAGE (not client) no longer swaps the thread — it's unified. This effect
  // only RE-SCOPES the section picker to the new page (the rail + `list` recompute from
  // effectivePage on their own). Resetting the pick is a correctness guard, not cosmetic: a
  // stale pick from the old page would otherwise make the next send target (new page,
  // old-page sectionId). Skips the initial mount + client switch (owned above) via the ref.
  useEffect(() => {
    const key = `${client.id}:${effectivePage}`;
    if (prevPageRef.current === key) return; // same (client,page) as last run → nothing to do
    prevPageRef.current = key;
    setSectionChoice('');
    setSectionName('');
    setActiveSectionId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivePage]);

  // #GR25 mount: restore the reader's scroll position (a cache paint), else land at the
  // latest. Layout-effect so it runs before paint — no visible jump. IA-3: per client only
  // (the unified thread doesn't change on a page switch, so don't reset scroll then).
  useLayoutEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    const cache = viewCache.get(viewKey(client.id, instance));
    if (cache && cache.scrollTop > 0) el.scrollTop = cache.scrollTop;
    else el.scrollTop = el.scrollHeight; // fresh view → start at the newest turn
    atBottomRef.current = nearBottom(el);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  // #GR25 auto-scroll: only when a genuinely NEW message arrives (or the working bubble
  // toggles) AND the reader is already near the bottom — never yank them mid-history.
  useEffect(() => {
    const el = threadRef.current;
    const newMessage = messages.length > prevLenRef.current;
    prevLenRef.current = messages.length;
    if (el && shouldAutoScroll({ newMessage, busy, atBottom: atBottomRef.current })) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length, busy]);

  // #GR25/#GR10: keep the view cache current (thread + draft + pick), so a remount paints
  // from it instantly. scrollTop is maintained by the scroller's onScroll below. IA-3: keyed
  // per (client, instance) — the unified thread.
  const cacheClientRef = useRef(client.id);
  useEffect(() => {
    // On the render where client.id JUST changed, this state still belongs to the PREVIOUS
    // client (the client effect re-seeds it on the next commit). Skip that one stale write
    // so we never stamp the old thread under the new key.
    if (cacheClientRef.current !== client.id) {
      cacheClientRef.current = client.id;
      return;
    }
    const key = viewKey(client.id, instance);
    const existing = viewCache.get(key);
    viewCache.set(key, {
      messages,
      input,
      sectionName,
      sectionChoice,
      activeSectionId,
      scrollTop: existing?.scrollTop ?? 0,
    });
  }, [client.id, instance, messages, input, sectionName, sectionChoice, activeSectionId]);

  // Record the reader's scroll position + near-bottom state (no re-render — refs + cache).
  function onThreadScroll() {
    const el = threadRef.current;
    if (!el) return;
    atBottomRef.current = nearBottom(el);
    const cache = viewCache.get(viewKey(client.id, instance));
    if (cache) cache.scrollTop = el.scrollTop;
  }

  // Reset the Stop pending-state + the live phase once the turn ends (activeTurn cleared).
  useEffect(() => {
    if (!activeTurn) {
      setStopping(false);
      setBuildPhase(null);
    }
  }, [activeTurn]);

  async function pollTurn(buildId: string, forClient: string, sectionId?: string) {
    const started = Date.now();
    // #GR43: keep refreshing the rail until THIS turn's section appears in it, then stop
    // (a 25-min build must not refetch the client every poll). A NEW section materialises
    // server-side only AFTER the decision turn (~10–40s) + the kickoff/clone hop —
    // routinely >30s — so the earlier flat 30s cap closed before the row existed. No
    // section id (a reply / SEO turn) → nothing to wait for.
    let railSettled = !sectionId;
    // #2 status-freshness: poll PAST the server's detached ceiling (DETACHED_MAX_MIN=45,
    // netlify/functions/_lib/builds.ts — the reconcile ages a detached row out to a
    // terminal outcome by then). The OLD 15-min budget was SHORTER than a cold detached
    // build (15–25 min, and chat-turn-status won't time out an engine_run row), so the
    // budget elapsed mid-build → the finally cleared activeTurn → the section chip fell
    // back to its PREVIOUS green commit and flashed "Ready to lock ✓" while a build was
    // still in flight. At 48 min the poll only ever returns on a GENUINE terminal status,
    // so activeTurn (→ the "🔨 Building…" chip) stays alive for the build's true lifetime.
    const BUDGET_MS = 48 * 60 * 1000; // > DETACHED_MAX_MIN (45) + margin
    while (Date.now() - started < BUDGET_MS) {
      const elapsed = Date.now() - started;
      await sleep(elapsed < 60_000 ? 4_000 : elapsed < 180_000 ? 8_000 : 15_000);
      if (clientIdRef.current !== forClient) return; // client switched — stop polling
      let st: { status: string; phase?: string | null; error?: string | null };
      try {
        st = await callApi<{ status: string; phase?: string | null; error?: string | null }>('chat-turn-status', {
          method: 'POST',
          body: JSON.stringify({ buildId }),
        });
      } catch {
        continue; // transient — keep polling
      }
      if (clientIdRef.current === forClient) setBuildPhase((st.phase as BuildPhase) ?? null); // #GR6 live label
      await loadThread(forClient); // surface the agent's reply/result as soon as it lands (unified)
      // #GR43: refresh the SECTION RAIL (re-read wizard_state.sections via the parent)
      // UNTIL this turn's new section appears in it — turn.ts materialises the row only
      // after the decision turn + kickoff/clone hop (~30s–2min), so a flat 30s cap missed
      // it and the section stayed invisible until a hard reload. loadThread refreshes the
      // thread, not the rail; onActivity (parent reloadClients) refreshes the rail. Once
      // the section is present we stop — so a long build doesn't refetch every poll.
      if (!railSettled && clientIdRef.current === forClient) {
        if (sectionId && sectionsRef.current[sectionId]) {
          railSettled = true; // it's in the rail now (from a prior refresh) → stop refreshing
        } else {
          onActivity(); // keep refreshing until the materialised section shows up
        }
      }
      if (st.status === 'success' || st.status === 'failed') {
        // #GR2: a dropped dispatch fails with no agent message in the thread — surface
        // the reason so it's never a silent spinner (the composer stays for retry).
        if (st.status === 'failed' && st.error && clientIdRef.current === forClient) setErr(friendlyError(st.error, st.error));
        // #GR37: the terminal agent message ("No change was needed" / the build result)
        // is posted ASYNCHRONOUSLY by the reconcile a beat AFTER it flips the outcome, so
        // the loadThread above (on the flip poll) can miss it — the message then only
        // appears on a manual reload. Re-fetch a couple more times so it lands on its own.
        let fresh: ChatMessage[] | undefined;
        for (const delay of [1500, 4000]) {
          await sleep(delay);
          if (clientIdRef.current !== forClient) break;
          fresh = (await loadThread(forClient)) ?? fresh;
        }
        // #GR65: if the agent REGISTERED a section named in prose this turn (set_section), the
        // reply carries meta.sectionRegistered — pre-select it so the next turn is ready to
        // build without re-picking. Best-effort + guarded: never override a section the
        // designer actively picked, and never during SEO.
        if (!isSeo && clientIdRef.current === forClient) {
          const reg = (fresh ?? [])
            .filter((m) => m.role === 'agent')
            .map((m) => m.meta as { sectionRegistered?: { sectionId: string; name: string } } | undefined)
            .reverse()
            .find((meta) => meta?.sectionRegistered)?.sectionRegistered;
          if (reg?.sectionId) {
            setActiveSectionId(reg.sectionId);
            setSectionChoice(reg.sectionId);
            setSectionName('');
          }
        }
        return;
      }
    }
  }

  // Re-attach to an in-flight turn (on remount): poll to completion, then clear the
  // persisted marker. Sets busy so the UI reflects the running build immediately.
  // IA-1: poll a running turn IN THE BACKGROUND (no `busy` — the composer stays live so
  // the designer keeps composing/queuing). On completion, clear the active turn and DRAIN
  // the queue (auto-fire the next captured send). Reused by the mount re-attach path.
  async function watchTurn(turn: ActiveTurn, forClient: string) {
    try {
      await pollTurn(turn.buildId, forClient, turn.sectionId); // #GR43: waits for the section (thread is unified, IA-3)
    } catch (e) {
      // IA-1: a rare pollTurn rejection is SURFACED (not left unhandled) — the finally
      // block below still clears the turn + drains the queue, so one failed poll never
      // wedges the queue.
      if (clientIdRef.current === forClient) setErr(friendlyError(e, (e as Error).message));
    } finally {
      if (clientIdRef.current === forClient) {
        setActiveTurn(null);
        saveTurn(forClient, instance, null);
        onActivity();
        drainQueue(forClient); // IA-1: auto-start the next queued send, if any
      }
    }
  }

  // IA-1: dispatch ONE send — upload + fire chat-turn (transient `busy`), set the active
  // turn, then hand off to a BACKGROUND poll (watchTurn). `busy` covers only the brief
  // dispatch, NOT the ~15–25-min build, so the composer re-enables immediately after.
  async function fireTurn(payload: QueuedSend, forClient: string) {
    setBusy(true);
    setErr(null);
    try {
      const uploadSectionId = resolveUploadSection(payload.sectionId, activeSectionId, isSeo);
      const attachments: string[] = [];
      for (let i = 0; i < payload.files.length; i++) {
        setPhase(`Uploading ${i + 1}/${payload.files.length}…`);
        const { base64, filename } = await prepareImageUpload(payload.files[i]);
        const up = await callApi<{ path: string }>('artboard-upload', {
          method: 'POST',
          body: JSON.stringify({ clientId: forClient, sectionId: uploadSectionId, pageId: payload.turnPage, filename, dataBase64: base64 }),
        });
        attachments.push(up.path);
      }
      const section = payload.sectionId
        ? { sectionId: payload.sectionId, name: payload.sectionName || payload.sectionId, order: sections[payload.sectionId]?.order ?? list.length }
        : undefined;
      setPhase('Sending…');
      const res = await callApi<{ buildId: string }>('chat-turn', {
        method: 'POST',
        body: JSON.stringify({ clientId: forClient, instance, message: payload.message, attachments, section, pageId: payload.turnPage }),
      });
      const turn: ActiveTurn = { buildId: res.buildId, sectionId: payload.sectionId, sectionName: payload.sectionName || undefined, pageId: payload.turnPage, siteOp: payload.siteOp };
      setActiveTurn(turn);
      saveTurn(forClient, instance, turn);
      await loadThread(forClient); // IA-3: unified thread — the user turn appears immediately
      onActivity();
      setBusy(false); // IA-1: composer LIVE now — the build runs in the background
      setPhase('');
      void watchTurn(turn, forClient); // background poll → drains the queue on completion
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
      setBusy(false);
      setPhase('');
      // Dispatch failed → nothing is running; still try to drain any queued sends.
      drainQueue(forClient);
    }
  }

  // IA-1: fire the next queued send (FIFO), else mark idle. Dequeue + fire happen OUTSIDE
  // setQueue (the updater stays pure — StrictMode double-invokes it in dev), driven by the
  // synchronous queueRef mirror. runningRef is the gate: true while the next fires, false
  // when the queue is empty. Guarded against a stale client.
  function drainQueue(forClient: string) {
    if (clientIdRef.current !== forClient) {
      runningRef.current = false;
      return;
    }
    if (queueRef.current.length === 0) {
      runningRef.current = false;
      return;
    }
    const [next, ...rest] = queueRef.current;
    queueRef.current = rest;
    setQueue(rest); // pure
    runningRef.current = true;
    void fireTurn(next, forClient);
  }

  // IA-1/IA-4: hand a batch of captured sends to the queue. If nothing is running, the first
  // fires now and the rest queue; else all queue. runningRef is synchronous so two rapid
  // sends can't both fire — never a 2nd heavy build in parallel.
  function enqueueSends(payloads: QueuedSend[]) {
    if (payloads.length === 0) return;
    if (runningRef.current) {
      queueRef.current = [...queueRef.current, ...payloads];
      setQueue(queueRef.current);
      return;
    }
    const [first, ...rest] = payloads;
    if (rest.length) {
      queueRef.current = [...queueRef.current, ...rest];
      setQueue(queueRef.current);
    }
    runningRef.current = true;
    void fireTurn(first, client.id);
  }

  async function send() {
    setErr(null);
    const message = input.trim();

    // IA-4: a WHOLE-SITE send fans out one per-(page, anchor-section) build turn per BUILT
    // page (multi-page.md §14). Each leg is a NORMAL build turn on a real anchor section →
    // canBuild true → the engine/turn path is unchanged. #GR57: each leg's message carries
    // the literal `[Whole-site change …]` marker — build-site/SKILL.md §5e keys the agent's
    // site-op behaviour off that exact prefix (apply the page-level change; don't rebuild the
    // anchor section — grounding owns that now, so no "do not regenerate" clause here).
    if (scope === 'site' && !isSeo) {
      if (!message) {
        setErr('Describe the site-wide change (e.g. “add the footer to every page”).');
        return;
      }
      if (message.length > MAX_MESSAGE_CHARS) {
        setErr(`Message is too long (max ${MAX_MESSAGE_CHARS.toLocaleString()} characters).`);
        return;
      }
      if (files.length > MAX_ATTACHMENTS) {
        setErr(`Too many attachments (max ${MAX_ATTACHMENTS}).`);
        return;
      }
      const targets = builtPageAnchors(client.wizard_state);
      if (targets.length === 0) {
        setErr('No built pages yet — build at least one section before applying a site-wide change.');
        return;
      }
      const payloads: QueuedSend[] = targets.map((t) => ({
        id: crypto.randomUUID(),
        // #GR57: the `[Whole-site change …]` marker is what build-site §5e keys on — keep it
        // verbatim. Grounding owns "apply the page-level change, don't rebuild the anchor".
        message: `[Whole-site change — applied to each page in turn] For the "${t.pageName}" page:\n\n${message}`,
        files: [...files], // the same reference(s) apply to each page (usually none for a site op)
        sectionId: t.anchorId,
        sectionName: t.anchorName,
        turnPage: t.pageId,
        label: t.anchorName,
        siteOp: true,
      }));
      // Clear the composer + drop back to page scope (avoid an accidental repeat fan-out).
      setInput('');
      clearFiles();
      setScope('page');
      enqueueSends(payloads);
      return;
    }

    if (!message && files.length === 0) {
      setErr('Type a message or attach a file.');
      return;
    }
    if (message.length > MAX_MESSAGE_CHARS) {
      setErr(`Message is too long (max ${MAX_MESSAGE_CHARS.toLocaleString()} characters).`);
      return;
    }
    if (files.length > MAX_ATTACHMENTS) {
      setErr(`Too many attachments (max ${MAX_ATTACHMENTS}).`);
      return;
    }
    // Resolve the section target from the picker NOW (the picker state may change before a
    // queued send fires). #GR24: existing id (iterate) / typed NEW name (create) / none.
    const { sectionId, label: sectionLabelText } = isSeo
      ? { sectionId: undefined as string | undefined, label: '' }
      : resolveSectionChoice(sectionChoice, sectionName, sections);
    // M-P3: this turn's page (Build → the active page; page-aware build writes its route).
    const turnPage = isSeo ? undefined : effectivePage;
    const payload: QueuedSend = {
      id: crypto.randomUUID(),
      message,
      files: [...files],
      sectionId,
      sectionName: sectionLabelText,
      turnPage,
      label: sectionLabelText || (sectionId ?? 'chat'),
    };

    // #GR26A: keep the sticky active section aligned with the pick.
    if (sectionId && sectionId !== activeSectionId) setActiveSectionId(sectionId);
    // #GR24: keep the picker on the section just targeted (name it once).
    if (sectionId) {
      setSectionChoice(sectionId);
      setSectionName('');
    }
    // Clear the composer — the send is captured (fired now or queued).
    setInput('');
    clearFiles();

    enqueueSends([payload]);
  }

  // #GR17: Stop the in-flight turn. Flags the build row; the agent loop breaks
  // before commit (nothing lands) and the poll then surfaces the "Stopped" reply +
  // clears activeTurn. The work is server-side, so this is a request, not an abort.
  async function stop() {
    if (!activeTurn || stopping) return;
    setStopping(true);
    try {
      await callApi('build-cancel', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, buildId: activeTurn.buildId }),
      });
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
      setStopping(false); // let them retry
    }
  }

  async function deleteSection(sectionId: string, name: string) {
    if (!window.confirm(`Delete the "${name}" section from the rail? Its committed code (if any) stays on the branch — ask the chat to remove it from the page.`)) {
      return;
    }
    setLockErr((m) => ({ ...m, [sectionId]: '' }));
    try {
      await callApi('build-chat-section-delete', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, sectionId, pageId: effectivePage }),
      });
      onActivity();
    } catch (e) {
      setLockErr((m) => ({ ...m, [sectionId]: friendlyError(e, (e as Error).message) }));
    }
  }

  async function lock(sectionId: string, buildId: string) {
    setLockErr((m) => ({ ...m, [sectionId]: '' }));
    try {
      await callApi('build-chat-lock', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, buildId, sectionId, pageId: effectivePage }),
      });
      onActivity();
    } catch (e) {
      setLockErr((m) => ({ ...m, [sectionId]: friendlyError(e, (e as Error).message) }));
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-2">
        {isSeo ? (
          <>
            Get found on Google — and by AI assistants. Tell the agent the business facts in chat; it
            researches keywords, competitors and the search setup, proposes the SEO decisions (you don’t
            answer SEO questions), pushes back where needed, then writes the handoff doc. It can also
            refresh SEO for a live client (it only writes the handoff, never the deployed site).
          </>
        ) : (
          <>
            Build the site by chatting, one section at a time. Name the section you’re working on, describe it
            (drop an artboard if you have one), and the agent builds it to the house rules — it’ll ask questions
            and push back where needed. Preview each section, iterate, then <b>lock</b> it once its build is green.
          </>
        )}
      </p>

      {liveBlocked && (
        <p className="rounded border border-warning-bright bg-warning-bright/10 p-2 text-sm text-warning">
          🔒 This site is live/protected — the build chat is read-only.
        </p>
      )}
      {isSeo && client.is_live && canMaintainLive && (
        <p className="rounded border border-warning-bright bg-warning-bright/10 p-2 text-sm text-warning">
          Live — maintenance mode: SEO refresh writes only the handoff doc on the preview; the deployed
          site isn’t touched. Applying it to the live site still needs a publish.
        </p>
      )}

      {/* Page manager (M-P2) — the site's pages as chips (Home always present) + New page.
          Click a page to make it the active page; the section rail below scopes to it.
          IA-2: shared PageManager (also the standalone "Site structure" wizard step) — the
          parent owns the active-page state so the rail scopes in lock-step. Build-only. */}
      {!isSeo && (
        <PageManager
          client={client}
          activePage={activePage}
          onSwitchPage={setActivePage}
          onActivity={onActivity}
          disabled={liveBlocked}
        />
      )}

      {/* M-P6: the secondary-page explainer banner is retired — the page row + scoped rail
          make the active page obvious, and secondary-page building is now the norm. */}

      {/* Persistent in-flight status — survives navigating away + back (re-attaches
          on return). The build/handoff completes server-side regardless. */}
      {activeTurn && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-info bg-info-bright/10 p-2 text-sm text-info">
          {/* #GR40: 🔨 only once a real build is in flight; 💭 while it's just deciding. */}
          <span className="animate-pulse" aria-hidden>{isBuildingPhase(buildPhase) ? '🔨' : '💭'}</span>
          <span className="min-w-0 flex-1">
            {stopping ? (
              <>Stopping… it’ll break before committing, so nothing lands.</>
            ) : (
              <>
                {isSeo ? (
                  <>Working on the SEO handoff…</>
                ) : activeTurn.siteOp ? (
                  // #GR58: a whole-site fan-out leg — the anchor section is just the build unit,
                  // so "Building <anchor>" would mislead. Frame it as the site change, per page.
                  <>
                    Applying site change · <b>{pageLabel(activeTurn.pageId)}</b>
                    {isBuildingPhase(buildPhase) && buildPhase ? <> — {PHASE_LABEL[buildPhase]}</> : <>…</>}
                  </>
                ) : isBuildingPhase(buildPhase) ? (
                  <>
                    Building {activeTurn.sectionName ? <b>{activeTurn.sectionName}</b> : 'your section'}
                    {/* #GR6: the live phase, so a 6–9-min run reads as progress, not a dead spinner. */}
                    {buildPhase ? <> — {PHASE_LABEL[buildPhase]}</> : <>…</>}
                  </>
                ) : (
                  // #GR40: the fast decision turn — replying / deciding, NOT building yet.
                  <>Thinking about {activeTurn.sectionName ? <b>{activeTurn.sectionName}</b> : 'your section'}…</>
                )}{' '}
                this runs in the background — you can switch steps or clients and come back; it’ll keep going.
              </>
            )}
          </span>
          {/* #GR17: Stop the in-flight turn — the loop breaks before commit_section. */}
          <button
            onClick={() => void stop()}
            disabled={stopping}
            className="flex-none rounded border border-warning-bright bg-paper px-3 py-1 text-xs font-medium text-warning hover:bg-warning-bright/10 disabled:opacity-50"
          >
            {stopping ? 'Stopping…' : '■ Stop'}
          </button>
        </div>
      )}

      {/* SEO: the client-presentable summary once a handoff is verified. */}
      {isSeo && seoSummary && <SeoSummary summary={seoSummary} />}

      {/* Section rail — ONE "Current site" link + per-section status + Lock. */}
      {list.length > 0 && (
        <div className="space-y-2">
          {/* #GR23: a single link to the CURRENT site — the branch's latest preview
              deployment (the full, current single page). Per-section immutable URLs were
              three snapshots of the same one-page site → read as "different pages". The
              lock still green-gates on each section's own commit (build-chat-lock). */}
          <div className="flex flex-wrap items-center gap-2 rounded border border-line bg-paper p-2 text-sm">
            <span className="font-medium text-ink-2">{isHomePage ? 'Current site' : `Current page (/${pages[effectivePage]?.slug ?? effectivePage})`}</span>
            <BuildStatus
              key={effectivePage}
              buildId={`site:${client.id}`}
              requestBody={{ clientId: client.id }}
              endpoint="site-status"
              initialUrl={null}
              // M-P4: deep-link the ACTIVE page's route (Home → root; else /<slug>).
              pathSuffix={isHomePage ? '' : `/${pages[effectivePage]?.slug ?? effectivePage}`}
              live
              onResolved={onActivity}
            />
          </div>
          <ol className="space-y-2">
          {list.map((s, i) => {
            // While a turn is running FOR THIS section, show its live state; else its
            // status is derived from lifecycle (locked) + its own build (chip-only).
            // #GR40: distinguish a real build in flight from the fast decision turn — the
            // chip only says "Building" once a build phase is live, else "Working…".
            const active = activeTurn?.sectionId === s.id;
            const building = active && isBuildingPhase(buildPhase);
            return (
            <li key={s.id} className="rounded border border-line bg-paper p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-line text-[10px] text-ink-2">
                  {i + 1}
                </span>
                <span className="font-medium text-ink">{s.name ?? s.id}</span>
                {active && activeTurn?.siteOp ? (
                  // #GR58: this section is only the build unit for a whole-site fan-out leg —
                  // don't say "Building <section>"; it's a site change applied to the page.
                  <span className="inline-flex items-center gap-1 rounded bg-info-bright/10 px-2 py-0.5 text-xs text-info">
                    <span className="animate-pulse" aria-hidden>🌐</span> Applying site change…
                  </span>
                ) : building ? (
                  <span className="inline-flex items-center gap-1 rounded bg-info-bright/10 px-2 py-0.5 text-xs text-info">
                    <span className="animate-pulse" aria-hidden>🔨</span> Building…
                  </span>
                ) : active ? (
                  // #GR40: a turn is live on this section but it's DECIDING, not building.
                  <span className="inline-flex items-center gap-1 rounded bg-info-bright/10 px-2 py-0.5 text-xs text-info">
                    <span className="animate-pulse" aria-hidden>💭</span> Working…
                  </span>
                ) : s.locked ? (
                  <span className="rounded bg-success-bright/10 px-2 py-0.5 text-xs text-success">Locked ✓</span>
                ) : s.buildId ? (
                  // #GR23: chip-only (no URL) — its live build status, so the designer sees
                  // building/failed/ready-to-lock and the lock affordance stays meaningful.
                  <BuildStatus key={s.buildId} buildId={s.buildId} initialUrl={null} live chipOnly readyLabel="Ready to lock ✓" onResolved={onActivity} />
                ) : (
                  <span className="rounded bg-info-bright/10 px-2 py-0.5 text-xs text-info">In progress</span>
                )}
                {!liveBlocked && (
                  <button
                    onClick={() => void deleteSection(s.id, s.name ?? s.id)}
                    title="Delete this section from the rail"
                    className="ml-auto rounded px-1.5 py-0.5 text-xs text-ink-2 hover:bg-danger-bright/10 hover:text-danger"
                  >
                    ✕
                  </button>
                )}
              </div>
              {/* Lock is offered only when NO turn is live on this section (building OR
                  deciding) — locking the prior build while a new turn is running is
                  exactly the staleness we're closing (#GR40: gate on `active`, not just
                  `building`, so a decision turn doesn't re-expose the lock). */}
              {!liveBlocked && !s.locked && !active && s.buildId && (
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => lock(s.id, s.buildId!)}
                    className="rounded bg-ink px-3 py-1.5 text-xs font-medium text-paper"
                    title="Locks once the Cloudflare build is green"
                  >
                    Lock section
                  </button>
                  <span className="text-xs text-ink-2">Locks only when its build is green.</span>
                </div>
              )}
              {lockErr[s.id] && <p className="mt-1 text-xs text-danger">{lockErr[s.id]}</p>}
            </li>
            );
          })}
          </ol>
        </div>
      )}

      {/* The thread — IA-3: for Build this is ONE unified conversation across every page
          (allPages), each bubble badged with its page (+ section); SEO is a single thread. */}
      <div ref={threadRef} onScroll={onThreadScroll} className="max-h-[28rem] space-y-3 overflow-y-auto rounded border border-line bg-paper-2 p-3">
        {loading ? (
          <p className="text-sm text-ink-2">Loading the conversation…</p>
        ) : messages.length === 0 ? (
          <p className="text-sm text-ink-2">
            No messages yet — say hello, or name a section and describe what to build.
          </p>
        ) : (
          messages.map((m) => {
            // IA-3 badges (build only): which page + section this bubble targeted, so the
            // unified log stays legible. Page shows on every build bubble (NULL → Home);
            // section only when the turn recorded one (meta.sectionId — agent build turns).
            const mSectionId = typeof m.meta?.sectionId === 'string' ? (m.meta.sectionId as string) : null;
            return (
            <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === 'user' ? 'bg-ink text-paper' : 'border border-line bg-paper text-ink'
                }`}
              >
                <div className="mb-0.5 flex flex-wrap items-center gap-1 text-[10px] uppercase tracking-wide opacity-60">
                  <span>{m.role === 'user' ? 'You' : 'Agent'}</span>
                  {!isSeo && (
                    <span
                      className={`rounded px-1 py-px normal-case ${m.role === 'user' ? 'bg-white/20' : 'bg-paper-2 text-ink-2'}`}
                      title={`Page: ${pageLabel(m.pageId)}`}
                    >
                      {pageLabel(m.pageId)}
                    </span>
                  )}
                  {!isSeo && mSectionId && (
                    <span
                      className={`rounded px-1 py-px normal-case ${m.role === 'user' ? 'bg-white/20' : 'bg-paper-2 text-ink-2'}`}
                      title={`Section: ${sectionLabel(m.pageId, mSectionId)}`}
                    >
                      §{sectionLabel(m.pageId, mSectionId)}
                    </span>
                  )}
                </div>
                {m.content}
                {m.attachments.length > 0 && (
                  <ul className="mt-1 flex flex-wrap gap-1">
                    {m.attachments.map((p) => (
                      <li
                        key={p}
                        className={`rounded px-1.5 py-0.5 text-[10px] ${
                          m.role === 'user' ? 'bg-white/20' : 'bg-paper-2 text-ink-2'
                        }`}
                      >
                        📎 {fileName(p)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            );
          })
        )}
        {busy && (
          <div className="flex justify-start">
            <div className="rounded-lg border border-line bg-paper px-3 py-2 text-sm text-ink-2">
              {phase || 'Agent is working…'}
            </div>
          </div>
        )}
      </div>

      {/* Pinned input */}
      {!liveBlocked && (
        <div className="space-y-2 rounded border border-line bg-paper p-3">
          {/* IA-4 scope toggle — "This page" (default) or "Whole site" (a send fans out one
              build turn per built page, each on its anchor section). Build only. */}
          {!isSeo && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-medium text-ink-2">Scope</span>
              <div className="inline-flex overflow-hidden rounded border border-line">
                <button
                  onClick={() => setScope('page')}
                  className={`px-2.5 py-1 ${scope === 'page' ? 'bg-ink text-paper' : 'bg-paper text-ink-2 hover:bg-paper-2'}`}
                >
                  This page
                </button>
                <button
                  onClick={() => setScope('site')}
                  className={`px-2.5 py-1 ${scope === 'site' ? 'bg-ink text-paper' : 'bg-paper text-ink-2 hover:bg-paper-2'}`}
                >
                  🌐 Whole site
                </button>
              </div>
            </div>
          )}
          {/* IA-3 active-context indicator (the steering wheel) — the thread is the whole-site
              log, so THIS makes it unmissable what the NEXT send targets, regardless of where
              the reader has scrolled. #GR56: ~50% gray (was near-black) — softer but still
              clearly noticeable. Build only; IA-4: shows the whole-site scope too. */}
          {!isSeo && (
            <div className="flex flex-wrap items-center gap-2 rounded-md bg-gray-500 px-3 py-2 text-sm text-white">
              <span aria-hidden>🎯</span>
              <span className="font-semibold uppercase tracking-wide text-[11px] text-gray-200">
                {scope === 'site' ? 'Applying to' : 'Building'}
              </span>
              {scope === 'site' ? (
                <>
                  <span className="rounded bg-white/20 px-2 py-0.5 font-medium">🌐 Whole site — every built page</span>
                  <span className="ml-auto text-[11px] text-gray-200">your next message applies to all pages</span>
                </>
              ) : (
                <>
                  <span className="rounded bg-white/20 px-2 py-0.5 font-medium">{pageLabel(effectivePage)}</span>
                  <span className="text-gray-200" aria-hidden>·</span>
                  <span className="rounded bg-white/20 px-2 py-0.5 font-medium">
                    {sectionChoice === NEW_SECTION
                      ? (sectionName.trim() || 'new section — name it below')
                      : sectionChoice
                        ? (sections[sectionChoice]?.name ?? sectionChoice)
                        : 'just chat — no section'}
                  </span>
                  <span className="ml-auto text-[11px] text-gray-200">your next message targets this</span>
                </>
              )}
            </div>
          )}
          {/* #GR24: pick a section to iterate (persists across sends → every turn targets
              it → #GR26A re-grounds its artboard every turn), or "+ New section" to start
              one. "Just chat" = no section. Hidden for SEO + when the scope is Whole site
              (IA-4: a site op targets each page's anchor section automatically). */}
          {!isSeo && scope === 'page' && (
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs font-medium text-ink-2">Section</label>
              <select
                value={sectionChoice}
                onChange={(e) => {
                  const v = e.target.value;
                  setSectionChoice(v);
                  // Keep the sticky-artboard section (#GR26A) aligned with the pick.
                  if (v && v !== NEW_SECTION) setActiveSectionId(v);
                  if (v !== NEW_SECTION) setSectionName('');
                }}
                className={inputCls}
              >
                <option value="">Just chat (no section)</option>
                {list.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name ?? s.id}
                    {s.locked ? ' ✓' : ''}
                  </option>
                ))}
                <option value={NEW_SECTION}>+ New section…</option>
              </select>
              {sectionChoice === NEW_SECTION && (
                <input
                  value={sectionName}
                  onChange={(e) => setSectionName(e.target.value)}
                  placeholder="New section name (e.g. Hero)"
                  autoFocus
                  className={`min-w-[10rem] flex-1 ${inputCls}`}
                />
              )}
            </div>
          )}
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !busy) void send();
            }}
            placeholder={
              isSeo
                ? 'Tell me about the business — name, what they do, where they’re based, services, contact…  (⌘/Ctrl+Enter to send)'
                : scope === 'site'
                  ? 'Describe a site-wide change — e.g. “add the footer to every page” or “apply the SEO across the site”.  (⌘/Ctrl+Enter)'
                  : 'Describe the section, give feedback, or ask a question…  (⌘/Ctrl+Enter to send)'
            }
            className={`h-24 w-full ${inputCls}`}
          />
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Attach {isSeo ? 'a logo / reference' : 'artboard(s) / reference'} — PNG/JPG/WEBP (optional)
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
              className="text-sm"
            />
          </label>
          {files.length > 0 && (
            <ul className="flex flex-wrap gap-2 text-xs text-ink-2">
              {files.map((f, i) => (
                <li key={i} className="rounded bg-paper-2 px-2 py-0.5">
                  {f.name} ({(f.size / 1024).toFixed(0)}KB)
                </li>
              ))}
            </ul>
          )}
          {/* Code-reference attachments (code-reference-attachments.md): drop reference CODE/TEXT
              the build agent READS as inspiration — committed under the picked section, sticky
              (mirrors artboards). Uploaded immediately on drop (not batched on send). Build only,
              page scope. */}
          {!isSeo && scope === 'page' && (
            <div className="flex flex-col gap-1 text-xs text-ink-2">
              <label className="flex flex-col gap-1">
                Reference code/text for the agent to read (optional) — code/text files, not images
                <input
                  ref={refInputRef}
                  type="file"
                  accept={REFERENCE_ACCEPT}
                  multiple
                  disabled={refBusy}
                  onChange={(e) => void uploadReferences(Array.from(e.target.files ?? []))}
                  className="text-sm"
                />
              </label>
              {!refSectionId && (
                <span className="text-ink-2">Pick or name a section above to attach references.</span>
              )}
              {refBusy && <span className="text-ink-2">Uploading reference…</span>}
              {references.length > 0 && (
                <ul className="flex flex-wrap gap-2">
                  {references.map((r) => (
                    <li key={r.name} className="inline-flex items-center gap-1 rounded bg-paper-2 px-2 py-0.5">
                      📎 {r.name}
                      <button
                        onClick={() => void deleteReference(r.name)}
                        disabled={refBusy}
                        title="Remove reference"
                        className="rounded px-1 text-ink-2 hover:bg-paper-2 hover:text-ink disabled:opacity-50"
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {refErr && <span className="text-danger">{refErr}</span>}
            </div>
          )}
          {/* IA-1: queued sends — captured while a turn runs, auto-start FIFO on completion. */}
          {queue.length > 0 && (
            <ul className="flex flex-wrap items-center gap-2 text-xs text-ink-2">
              <span className="text-ink-2">Queued (starts when the current one finishes):</span>
              {queue.map((q) => (
                <li key={q.id} className="inline-flex items-center gap-1 rounded bg-info-bright/10 px-2 py-0.5 text-info">
                  {/* IA-3 §13.3: show the captured page on the chip so a queued send reads as
                      targeting its OWN page even if the active page changes while it waits.
                      IA-4: a 🌐 marks a leg of a whole-site fan-out. */}
                  ⏳ {q.siteOp && <span aria-hidden>🌐</span>}{!isSeo && <span className="font-medium">{pageLabel(q.turnPage)} ·</span>} {q.label || 'chat'}
                  <button
                    onClick={() => {
                      queueRef.current = queueRef.current.filter((x) => x.id !== q.id);
                      setQueue(queueRef.current);
                    }}
                    title="Remove from the queue"
                    className="rounded px-1 text-info hover:bg-info-bright/10 hover:text-info"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center gap-3">
            <button
              onClick={() => void send()}
              disabled={busy}
              className={btnSolid}
            >
              <FlapText>{busy ? phase || 'Working…' : scope === 'site' ? 'Apply to all pages 🌐' : activeTurn ? 'Queue ⏳' : 'Send'}</FlapText>
            </button>
            {err && <p className="text-sm text-danger">{err}</p>}
          </div>
          <p className={sectionDesc}>
            {activeTurn
              ? 'A turn is running in the background — keep composing: your next message is queued and starts automatically when it finishes.'
              : isSeo
                ? 'The agent runs in the background (~1–3 min). It’ll confirm the facts, research, then write the handoff doc once everything’s aligned.'
                : 'The agent runs in the background — you can keep composing while it works. Name a section to have it built; leave the name blank to just talk it through first.'}
          </p>
        </div>
      )}

      {/* The gate */}
      {isSeo ? (
        <div className="rounded border border-dashed border-line p-4 text-sm">
          {seoDone ? (
            <div className="space-y-2">
              <p className="text-success">✓ SEO handoff written and verified.</p>
              <button onClick={onContinue} className={btnSolid}>
                <FlapText>Continue →</FlapText>
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-ink-2">
                No SEO handoff yet — give the agent the business facts in the chat above. This step is optional
                and won’t hold up the build.
              </p>
              {onSkip && (
                <button onClick={onSkip} className={btnGhost}>
                  Skip for now →
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
      <div className="rounded border border-dashed border-line p-4 text-sm">
        {everyLocked ? (
          <div className="space-y-2">
            <p className="text-success">✓ Every page’s sections are locked and green.</p>
            <button onClick={onContinue} className={btnSolid}>
              <FlapText>Continue to Preview &amp; approve →</FlapText>
            </button>
          </div>
        ) : (
          <p className="text-ink-2">
            {list.length === 0 ? (
              'No sections locked yet — build your first one in the chat above.'
            ) : blockingPages.length > 0 ? (
              <>
                Lock every section on{' '}
                {blockingPages.map((name, i) => (
                  <span key={name}>
                    {i > 0 && (i === blockingPages.length - 1 ? ' and ' : ', ')}
                    <b>{name}</b>
                  </span>
                ))}{' '}
                to continue.
              </>
            ) : (
              `${list.filter((s) => s.locked).length}/${list.length} sections locked on this page. Lock every page’s sections to continue.`
            )}
          </p>
        )}
      </div>
      )}
    </div>
  );
}
